<ng-container *ngIf="!loading && dashboard as data; else loadingState">
  <section class="page-heading">
    <div>
      <span class="eyebrow">{{ data.organization.name }}</span>
      <h1>Good to see you, {{ data.organization.recruiterName.split(' ')[0] }}.</h1>
      <p>Review your talent pool through the roles and signals that matter to your organization.</p>
    </div>
    <a class="primary-action" routerLink="/recruitment/benchmarks" queryParamsHandling="preserve">
      View role rankings
      <mat-icon>arrow_forward</mat-icon>
    </a>
  </section>

  <section class="metrics-grid" aria-label="Recruitment summary">
    <article class="metric-card primary-metric">
      <div class="metric-icon"><mat-icon>groups</mat-icon></div>
      <div><span>Total candidates</span><strong>{{ data.summary.totalCandidates }}</strong></div>
      <small>Organization talent pool</small>
    </article>
    <article class="metric-card">
      <div class="metric-icon success"><mat-icon>task_alt</mat-icon></div>
      <div><span>Assessment ready</span><strong>{{ data.summary.assessedCandidates }}</strong></div>
      <small>{{ data.summary.assessmentUnavailable }} awaiting assessment</small>
    </article>
    <article class="metric-card">
      <div class="metric-icon violet"><mat-icon>trending_up</mat-icon></div>
      <div><span>Move ahead</span><strong>{{ data.summary.moveAhead }}</strong></div>
      <small>Across all role reviews</small>
    </article>
    <article class="metric-card">
      <div class="metric-icon amber"><mat-icon>pause_circle</mat-icon></div>
      <div><span>On hold</span><strong>{{ data.summary.hold }}</strong></div>
      <small>{{ data.summary.rejected }} candidates rejected</small>
    </article>
  </section>

  <section class="dashboard-grid">
    <div class="main-column">
      <div class="section-heading">
        <div><h2>Private role benchmarks</h2><p>Select a role to see its best-fit candidates.</p></div>
        <a routerLink="/recruitment/benchmarks" queryParamsHandling="preserve">View all <mat-icon>chevron_right</mat-icon></a>
      </div>

      <div class="benchmark-grid">
        <a
          class="benchmark-card"
          *ngFor="let benchmark of data.benchmarks | slice:0:3; trackBy: trackByBenchmark"
          [routerLink]="['/recruitment/benchmarks', benchmark.benchmarkId]" queryParamsHandling="preserve">
          <div class="benchmark-topline">
            <span class="department">{{ benchmark.department }}</span>
            <span class="status" [class.recalculating]="benchmark.status === 'RECALCULATING'">
              <span></span>{{ benchmark.status === 'READY' ? 'Ready' : 'Refreshing' }}
            </span>
          </div>
          <h3>{{ benchmark.roleTitle }}</h3>
          <p>{{ benchmark.description }}</p>
          <div class="trait-list">
            <span *ngFor="let trait of benchmark.topTraits | slice:0:2">{{ trait }}</span>
          </div>
          <div class="benchmark-footer">
            <div><strong>{{ benchmark.evaluatedCount }}</strong><span> evaluated</span></div>
            <mat-icon>arrow_outward</mat-icon>
          </div>
        </a>
      </div>

      <div class="section-heading candidates-heading">
        <div><h2>Recently joined</h2><p>The newest candidates in your organization pool.</p></div>
        <a routerLink="/recruitment/candidates" queryParamsHandling="preserve">Talent pool <mat-icon>chevron_right</mat-icon></a>
      </div>

      <div class="candidate-table-card">
        <div class="candidate-row table-header">
          <span>Candidate</span><span>Assessment</span><span>Best role fit</span><span>Joined</span><span></span>
        </div>
        <div class="candidate-row" *ngFor="let candidate of data.recentCandidates; trackBy: trackByCandidate">
          <div class="candidate-cell">
            <span class="avatar">{{ initials(candidate.firstName, candidate.lastName) }}</span>
            <div><strong>{{ candidate.firstName }} {{ candidate.lastName }}</strong><small>{{ candidate.currentTitle || candidate.email }}</small></div>
          </div>
          <div>
            <span class="availability" [class.pending]="candidate.assessmentAvailability === 'UNAVAILABLE'">
              {{ candidate.assessmentAvailability === 'AVAILABLE' ? 'Ready' : 'Not available' }}
            </span>
          </div>
          <div class="fit-cell" *ngIf="candidate.bestFit; else noFit">
            <strong>{{ candidate.bestFit.matchScore }}%</strong><span>{{ candidate.bestFit.roleTitle }}</span>
          </div>
          <ng-template #noFit><span class="muted">Waiting for assessment</span></ng-template>
          <span>{{ candidate.joinedAt | date:'MMM d' }}</span>
          <a class="row-action" [routerLink]="['/recruitment/candidates', candidate.userId]" queryParamsHandling="preserve" aria-label="View candidate"><mat-icon>chevron_right</mat-icon></a>
        </div>
      </div>
    </div>

    <aside class="link-panel">
      <div class="link-panel-heading">
        <div class="link-icon"><mat-icon>link</mat-icon></div>
        <div><h2>Organization link</h2><p>Grow your shared talent pool.</p></div>
      </div>

      <ng-container *ngIf="data.organizationLink as link; else noLink">
        <ng-container *ngIf="!link.revoked; else revokedLink">
          <div class="qr-wrap" *ngIf="qrCodeDataUrl"><img [src]="qrCodeDataUrl" alt="Organization invitation QR code"></div>
          <p class="link-helper">Candidates can scan this QR or open the link to join {{ data.organization.name }} on Konvac.</p>
          <div class="link-field"><span>{{ link.url }}</span><button type="button" (click)="copyLink()"><mat-icon>content_copy</mat-icon></button></div>
          <div class="link-meta"><span><strong>{{ link.joinCount }}</strong> joined</span><span>Expires {{ link.expiresAt | date:'MMM d, y' }}</span></div>
          <button class="danger-link" type="button" [disabled]="updatingLink" (click)="revokeLink()">Revoke link</button>
        </ng-container>
        <ng-template #revokedLink>
          <div class="empty-link"><mat-icon>link_off</mat-icon><strong>Link revoked</strong><span>Create a new link when you are ready to invite candidates.</span></div>
          <button class="create-link" type="button" [disabled]="updatingLink" (click)="createLink()">Create new link</button>
        </ng-template>
      </ng-container>
      <ng-template #noLink>
        <div class="empty-link"><mat-icon>add_link</mat-icon><strong>No active link</strong><span>Create a reusable URL for your organization.</span></div>
        <button class="create-link" type="button" [disabled]="updatingLink" (click)="createLink()">Create organization link</button>
      </ng-template>
    </aside>
  </section>
</ng-container>

<ng-template #loadingState>
  <div class="loading-state" *ngIf="loading"><mat-spinner diameter="36"></mat-spinner><span>Preparing your workspace…</span></div>
  <div class="error-state" *ngIf="!loading && errorMessage"><mat-icon>cloud_off</mat-icon><h2>{{ errorMessage }}</h2><button type="button" (click)="loadDashboard()">Try again</button></div>
</ng-template>
